import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "../table/styles.css";
import "./skeleton.css";
import { themeQuartz, iconSetMaterial } from "ag-grid-community";
import { colors } from "../../theme/colors.js";
import { useScreen } from "../../components/Utils/Screens.js";
import { useLocation } from "react-router-dom";
import OptionsButton from "../options-button/index.js";

const myTheme = themeQuartz.withPart(iconSetMaterial).withParams({
  accentColor: colors.primaryColor,
  borderColor: colors.strokeLight,
  browserColorScheme: "light",
  columnBorder: false,
  fontFamily: "Figtree, sans-serif",
  foregroundColor: "#303030",
  headerBackgroundColor: colors.white,
  headerFontSize: 14,
  headerFontWeight: 500,
  headerRowBorder: true,
  headerTextColor: colors.lightText,
  headerVerticalPaddingScale: 1,
  oddRowBackgroundColor: colors.white,
  rowBorder: true,
  rowVerticalPaddingScale: 1,
  wrapperBorder: false,
});

const SkeletonCell = () => <div className="skeleton-cell" />;

export const Table = ({
  rowData,
  columnDefs,
  onRowClicked,
  pagination = true,
  loading = false,
  serverSideSort = false,
  sortState,
  onSortChange,
  screen,
}) => {
  const gridApiRef = useRef(null);
  const isPhone = !useScreen("sm");
  const location = useLocation();
  const pathName = location.pathname.split("/")[1];

  const isFitGrid =
    pathName === "DistributorDashboard" ||
    pathName === "PaymentsDashboard" ||
    pathName === "ManufacturerDashboard" ||
    pathName === "ASUsers" ||
    pathName === "Users" ||
    pathName === "CommissionStatements" ||
    pathName === "CommissionStatementsDetails" ||
    pathName === "DistributorCommissionStatements" ||
    pathName === "CommissionStatementsDistributor" ||
    pathName === "CommissionStatementsProducts" ||
    pathName === "InvoiceHome" ||
    pathName === "ReportsDashboard" ||
    pathName === "Reports" ||
    pathName === "BannerAlerts" ||
    pathName === "ShippingSetup";

  const isLastColumnFit =
    pathName === "IVRDetails" ||
    pathName === "IVRProduct" ||
    pathName === "IVRDashboard";

  const isFirstColumnFit =
    screen === "AccountHomeInvoiceTable" ||
    screen === "AccountHomeRecentOrders" ||
    screen === "ChildAccounts" ||
    screen === "AccountManufacturer" ||
    screen === "AccountUsers" ||
    screen === "AccountProductCatalog" ||
    screen === "AccountProductLog" ||
    screen === "ManufacturerUsers" ||
    screen === "ManufacturerAccountDashboard" ||
    screen === "ManufacturerProductGroups" ||
    screen === "ManufacturerStockLog";

  const SKELETON_ROW_COUNT = 8;

  const skeletonRows = useMemo(() => {
    return Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => ({
      __skeleton: true,
      id: `skeleton-${index}`,
    }));
  }, []);

  const processedColumnDefs = useMemo(() => {
    const lastColumnIndex = columnDefs?.length - 1;

    return columnDefs?.map((col, index) => {
      const isLastColumn = index === lastColumnIndex;
      const isFirstColumn = index === 0;

      const lastColumnProps =
        isLastColumnFit && isLastColumn
          ? {
              flex: 1,
              minWidth: col.minWidth || 300,
            }
          : {};

      const firstColumnProps =
        isFirstColumnFit && isFirstColumn
          ? {
              flex: 1,
              minWidth: col.minWidth || 200,
            }
          : {};

      const sortable =
        col.sortable === false
          ? false
          : serverSideSort
            ? Boolean(col.sortable) && !loading
            : !loading;

      return {
        ...col,
        ...lastColumnProps,
        ...firstColumnProps,
        sortable,
        comparator:
          serverSideSort
            ? () => 0
            : col.comparator,
        sort:
          serverSideSort && sortState?.field === col.field
            ? sortState.order
            : undefined,
        filter: col.filter === false ? false : !loading,
        cellRenderer: (params) => {
          if (params.data?.__skeleton) {
            return <SkeletonCell />;
          }

          return col.cellRenderer
            ? col.cellRenderer(params)
            : params.value ?? "";
        },
      };
    });
  }, [columnDefs, loading, isLastColumnFit, isFirstColumnFit, serverSideSort, sortState]);

  const autoSizeStrategy = useMemo(() => {
    return isFitGrid && !isPhone
      ? { type: "fitGridWidth" }
      : { type: "fitCellContents" };
  }, [isFitGrid, isPhone]);

  // AG Grid's built-in fitCellContents strategy measures every column,
  // including flex ones, and would overwrite the fitted first/last column's
  // flex width with a fixed pixel width. Skip it there; the effect below
  // (which is flex-aware) handles sizing instead.
  const gridAutoSizeStrategy =
    isFirstColumnFit || isLastColumnFit ? undefined : autoSizeStrategy;

  const applyColumnSizing = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || loading) return;

    const cols = api.getAllGridColumns();
    if (!cols || !cols.length) return;

    if (autoSizeStrategy?.type === "fitGridWidth") {
      api.sizeColumnsToFit();
    } else {
      // Columns with a flex colDef (e.g. the fitted first/last column)
      // manage their own width; auto-sizing them here would overwrite
      // flex with a fixed pixel width and leave dead space in the grid.
      const colIdsToAutoSize = cols
        .filter((col) => !col.getColDef().flex)
        .map((col) => col.getColId());

      api.autoSizeColumns(colIdsToAutoSize);
    }
  }, [autoSizeStrategy, loading]);

  useEffect(() => {
    if (!gridApiRef.current) return;
    if (loading) return;

    // Deferred to a macrotask so sizing runs after React commits; the
    // onFirstDataRendered / onRowDataUpdated handlers below are the primary
    // trigger (they fire once cells are actually painted, which is required
    // for autoSizeColumns to measure real cell content instead of just the
    // header). This effect is a fallback for cases where those don't fire.
    const timeoutId = setTimeout(applyColumnSizing, 0);
    return () => clearTimeout(timeoutId);
    // rowData is included so columns re-fit once async data arrives; without it
    // the sizing runs only on mount (while rows are still empty) and every
    // column is left at AG Grid's default width instead of fitting its content.
  }, [loading, applyColumnSizing, rowData]);

  const gridOptions = {
    enableCellTextSelection: true,
    ensureDomOrder: true,
    suppressCellFocus: loading,
    suppressMultiSort: serverSideSort,
    // Render all columns (no horizontal virtualisation) so autoSizeColumns can
    // measure every column's content. Otherwise off-screen columns in a wide
    // table are never measured and stay at AG Grid's default width. Column
    // counts here are small, so there is no meaningful performance cost.
    suppressColumnVirtualisation: true,
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
  };

  const onSortChanged = (params) => {
    if (!serverSideSort || loading) return;

    const sortedColumn = params.api
      .getColumnState()
      .find((column) => column.sort);

    if (!sortedColumn) {
      onSortChange?.(null);
      return;
    }

    onSortChange?.({
      field: sortedColumn.colId,
      order: sortedColumn.sort,
    });
  };

  return (
    <div>
      <AgGridReact
        rowData={loading ? skeletonRows : rowData}
        columnDefs={processedColumnDefs}
        pagination={pagination}
        paginationPageSize={20}
        paginationAutoPageSize={isPhone}
        domLayout="autoHeight"
        autoSizeStrategy={gridAutoSizeStrategy}
        theme={myTheme}
        gridOptions={gridOptions}
        defaultColDef={{
          resizable: true,
        }}
        onRowClicked={loading ? undefined : onRowClicked}
        onGridReady={onGridReady}
        onFirstDataRendered={applyColumnSizing}
        onRowDataUpdated={applyColumnSizing}
        onSortChanged={onSortChanged}
      />
    </div>
  );
};

export default Table;
